import { FC, SyntheticEvent, ReactElement, useState } from 'react';
import { SxProps } from '@mui/system/styleFunctionSx/styleFunctionSx';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import Draggable from 'react-draggable';
import Paper, { PaperProps } from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const BootstrapDialogTitle: FC<{
  children: ReactElement | ReactElement[] | string;
  onClose?: () => void;
}> = ({ children, onClose, ...other }) => {
  return (
    <DialogTitle
      id="draggable-dialog-title"
      sx={{ m: 0, px: 2, py: 1, backgroundColor: '#f0f0f0' }} {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}
const kDefWidth = { xs: 480, sm: 600, md: 750 };
type ModalDialogProps = {
  open: boolean;
  title?: ReactElement | ReactElement[] | string;
  content: ReactElement | ReactElement[] | string;
  sxx?: SxProps;
  buttons?: ReactElement | ReactElement[];
  onClose: (id: string) => void;
  defWidth?: number;
  defHeight?: number;
};
const ModalDialog: FC<ModalDialogProps> = ({ open, title, content, sxx, buttons, onClose, defWidth, defHeight }) => {
  const [width, setWidth] = useState<number>(defWidth ?? 600);
  const [height, setHeight] = useState<number>(defHeight ?? 350);
  const handleClose = (id: string): void => {
    if (onClose) {
      onClose(id);
    }
  }
  const handleMouseResize = (evt: SyntheticEvent, data: ResizeCallbackData) => {
    const deltaY = parseInt(Reflect.get(evt, "movementY"));
    const deltaX = parseInt(Reflect.get(evt, "movementX"));
    setHeight(height + deltaY);
    setWidth(width + deltaX);
  }
  const getTitle = (): ReactElement => {
    if (!title) {
      return <>Dialog</>;
    } else if (typeof title === 'string') {
      return <>{title}</>
    } else if (typeof title === 'object' && 'type' in title && 'props' in title) {
      return title;
    } else {
      return <>{title}</>
    }
  }
  return (
    <Dialog
      open={open}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={PaperComponent}
      maxWidth={false}
    >
      <Resizable
        height={height}
        width={width}
        onResize={handleMouseResize}
      >
        <>
          <BootstrapDialogTitle onClose={() => handleClose('sys-close')}>
            {getTitle()}
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <Box sx={sxx} height={`${height}px`} width={`${width}px`}>
              {content}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: '1.25rem', py: 1, backgroundColor: '#f0f0f0' }}>
            {buttons ? buttons :
              <Button color="secondary" onClick={() => handleClose('btn-close')} variant="contained">
                Close
              </Button>
            }
          </DialogActions>
        </>
      </Resizable>
    </Dialog>
  );
};

export default ModalDialog;